import { EmojiClickData, Theme } from "emoji-picker-react";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { EmojiIcon } from "@knowt/syncing/graphql/schema";
import CircularButton, { CircularButtonProps } from "@/components/CircularButton";
import { useThemeContextSelector } from "@/utils/theme";
import { themeColors } from "@/utils/themeColors";
import Popper from "@mui/material/Popper";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import CircularRectTextButton from "./CircularButton/styled/CircularRectTextButton";

const Picker = dynamic(() => import("emoji-picker-react"), { ssr: false });

type EmojiPickerProps = {
    onIconChange: ({ icon }: { icon: EmojiIcon | null }) => void;
    icon?: EmojiIcon | null;
    placeholder: React.ReactNode;
    disabled?: boolean;
    btnProps?: CircularButtonProps;
};

const EmojiPicker = ({
    onIconChange,
    icon,
    placeholder = null,
    disabled = false,
    btnProps: btnProps_ = { radius: "3rem", sx: {} },
}: EmojiPickerProps) => {
    const theme = useThemeContextSelector(state => state.theme);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [chosenEmoji, setChosenEmoji] = useState<EmojiIcon | null>(icon ?? null);

    useEffect(() => {
        setChosenEmoji(icon ?? null);
    }, [icon]);

    const handlePickEmoji = (emojiData: EmojiClickData | null) => {
        if (emojiData) {
            const emoji = {
                emoji: emojiData.emoji,
                skinTone: emojiData.activeSkinTone,
            };

            setChosenEmoji(emoji as EmojiIcon);

            onIconChange({ icon: emoji as EmojiIcon });
        } else {
            onIconChange({ icon: null });
        }
        closePicker();
    };

    const openPicker = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const closePicker = () => {
        setAnchorEl(null);
    };

    const isOpen = Boolean(anchorEl);

    const { radius, sx, ...btnProps } = btnProps_;

    return (
        <ClickAwayListener onClickAway={closePicker}>
            <div data-testid="auto-id-796a65ee-1191-46b7-9913-af09f22de7ca">
                <CircularButton
                    disabled={disabled}
                    onClick={openPicker}
                    sx={{ backgroundColor: "transparent", fontSize: "2rem", ...sx }}
                    radius={radius}
                    {...btnProps}>
                    {chosenEmoji?.emoji ?? placeholder}
                </CircularButton>

                <Popper open={isOpen} anchorEl={anchorEl}>
                    {chosenEmoji?.emoji && (
                        <CircularRectTextButton
                            sx={{
                                padding: "1rem 1rem 2rem",
                                borderRadius: chosenEmoji?.emoji ? "1rem 1rem 0 0" : "0 0 1rem 1rem",
                                marginBottom: "-1rem",
                                backgroundColor: themeColors.neutralWhite,
                                border: `1px solid ${themeColors.neutral1}`,
                                width: "100%",
                            }}
                            onClick={() => handlePickEmoji(null)}>
                            {"Remove Icon"}
                        </CircularRectTextButton>
                    )}
                    <Picker
                        style={{
                            borderRadius: !chosenEmoji?.emoji ? "1rem" : "0 0 1rem 1rem",
                            fontFamily: "var(--knowt-font-name)",
                        }}
                        lazyLoadEmojis={true}
                        theme={theme as Theme}
                        searchPlaceHolder={"Search with descriptions!"}
                        onEmojiClick={handlePickEmoji}
                    />
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

export default EmojiPicker;
